import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Reset password · RigBooks";
  }, []);

  const requestReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Enter the email on your account.");
    setBusy(true);
    try {
      const r = await api("/auth/forgot-password", {
        method: "POST",
        body: { email: email.trim() },
        auth: false,
      });
      if (r.reset_token) setToken(r.reset_token);
      toast.success(
        r.reset_token
          ? "Reset code generated — it's shown below. Expires in 1 hour."
          : "If that email is on file, a reset code has been issued.",
      );
      setStep(2);
    } catch (err) {
      toast.error(err?.message || "Couldn't request reset.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!token.trim() || newPassword.length < 6) {
      return toast.error("Paste the code and choose a 6+ character password.");
    }
    setBusy(true);
    try {
      await api("/auth/reset-password", {
        method: "POST",
        body: { token: token.trim(), new_password: newPassword },
        auth: false,
      });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-rig-bg px-5 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <Logo size={44} />
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-rig-dim">
            {step === 1 ? "Reset your password" : "Enter your reset code"}
          </p>
        </div>
        <div className="rounded-3xl border border-rig-border bg-rig-surface p-7 sm:p-9">
          {step === 1 ? (
            <form onSubmit={requestReset} className="space-y-4">
              <div>
                <Label htmlFor="email" className="font-display text-xs uppercase tracking-widest text-rig-dim">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="driver@example.com"
                  data-testid="forgot-email-input"
                  className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="h-12 w-full bg-rig-accent font-display text-base font-bold uppercase tracking-[0.2em] text-rig-accentText hover:bg-rig-accentDark"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset code"}
              </Button>
              <p className="text-center text-xs text-rig-mute">
                No email service is wired in this build — the code is shown on the next screen.
              </p>
            </form>
          ) : (
            <form onSubmit={submitReset} className="space-y-4">
              {token && (
                <div
                  data-testid="forgot-token-display"
                  className="rounded-xl border border-rig-accent bg-rig-bg p-3"
                >
                  <div className="font-display text-[10px] font-bold uppercase tracking-widest text-rig-accent">
                    Your reset code
                  </div>
                  <div className="mt-1 break-all text-xs text-rig-accent">{token}</div>
                </div>
              )}
              <div>
                <Label htmlFor="token" className="font-display text-xs uppercase tracking-widest text-rig-dim">
                  Reset code
                </Label>
                <Input
                  id="token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste the code from above"
                  data-testid="reset-token-input"
                  className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
                />
              </div>
              <div>
                <Label htmlFor="newpw" className="font-display text-xs uppercase tracking-widest text-rig-dim">
                  New password
                </Label>
                <Input
                  id="newpw"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  data-testid="reset-password-input"
                  className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="reset-submit"
                className="h-12 w-full bg-rig-accent font-display text-base font-bold uppercase tracking-[0.2em] text-rig-accentText hover:bg-rig-accentDark"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Set new password"}
              </Button>
            </form>
          )}
          <div className="mt-6 text-center text-sm text-rig-dim">
            Back to{" "}
            <Link to="/login" className="font-semibold text-rig-accent hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
